import React, { ChangeEvent, useEffect, useState } from 'react';
import { menuClasses, SubMenu } from 'react-pro-sidebar';
import styled from 'styled-components';
// Use shared Icon once, alias to avoid identifier collisions
import { Icon as SharedIcon } from 'shared/theme/link-ui-react/Icon';
import { Button, FormControl, Label, Text } from '@uhg-harmony/uitk-react';
import {
  CUSTOM_THEME_LIMIT,
  DEFAULT_CUSTOM_THEME_DATA,
  defaultLinkWidgetHoverData,
  widgetTypes,
} from 'builder/util/constants';
import { getCombinedFontsForTheme } from 'builder/util/getCombinedFonts';
import {
  horizonOptumTheme,
  horizonRallyTheme,
  horizonUhcTheme,
  optumTheme,
  optumThemeBar,
  rallyTheme,
  rallyThemeBar,
  uhcTheme,
  uhcThemeBar,
  darkTheme,
  darkThemeBar,
  // darkThemeColors,
} from './BrandThemes';
import useHandleColorSave from './useHandleColorSave';
import {
  StyledClose,
  StyledPickerHeader,
  StyledPickerPopover,
  StyledPickerWrapper,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import { IconPalette } from '@tabler/icons-react';
import { FontSelect } from '../SettingsPanel/SettingsPanel';
import {
  FontSizeInput,
  FontSizeInputWrapper,
  FontSizeUnitStyle,
  handleNumberInput,
} from 'builder/components/WidgetConfigurations/Typography/Typography';
import CustomThemesSection from './CustomThemesSection';
import StylePanelGradientColorPicker from 'builder/components/StylePanelGradientColorPicker.tsx/StylePanelGradientColorPicker';
import { defaultAndCustomColors } from 'shared/util/theme';
import {
  textColor,
  determineThemeKeyFromEditColorsLinkId,
  convertToHex,
} from 'builder/util/themeUtils';
import { StyledHSBar, StyledLink } from 'builder/util/StyledComponents';
import { ThemeType, harmonyThemeMap } from 'shared/util/ThemeType';
import { HarmonyTheme } from '@uhg-harmony/foundations';
import { ensureThemeText, getAvailableThemes } from 'builder/util/resolveTheme';
import { defaultTheme } from 'shared/theme/theme';

const SiteStyles = <IconPalette size={36} stroke={2} color="#2E3034" />;

const StyledMenuItem = styled(SubMenu)`
  margin-top: 1rem;
`;

StyledMenuItem.displayName = 'submenu';

export const StyledHeading = styled.p`
  color: ${defaultTheme.colors.aux.black};
  font-weight: 900;
  font-size: 1.3rem;
  font-family: OptumSans, sans-serif;
`;

export const TopSection = styled.div`
  display: flex;
  align-items: center;
  padding-top: 1.125rem;
  padding-left: 1.25rem;
`;

TopSection.displayName = 'top-section';

export const FieldLabel = styled(Label)`
  padding: 0.75rem 0 0 0.5rem;
  font-family: OptumSans, sans-serif;
  font-size: 0.875rem;
  justify-items: flex-start;
`;

export const ThemeColorCard = styled.div`
  border-style: solid;
  border-radius: 0.375rem;
  border-width: thin;
  font-family: OptumSans, sans-serif;
  border-color: ${defaultTheme.colors.aux.lightGrey};
  margin: 0.5rem;
`;

export const ColorCardTop = styled.div`
  display: inline;
  justify-content: space-between;
  align-items: center;
`;

export const ThemeSelectButton = styled(Button)<{ themeEnabled: boolean }>`
  line-height: 0.25;
  font-family: OptumSans, sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  margin: 0.5rem;
  padding: 0.25rem 1rem;
  background: ${p => (p.themeEnabled ? 'rgb(25, 110, 207)' : 'transparent')};
  color: ${p => (p.themeEnabled ? 'rgb(255, 255, 255)' : 'rgb(0, 38, 119)')};
`;

// Font selectors hidden for now
const FontDropdowns = styled.div`
  display: none;
  padding: 0.5rem;
`;

export const LinkWrapper = styled.div`
  display: flex;
  padding: 0 0.5rem;
  justify-content: space-between;
`;

export const StyledMenuLink = styled(Text)`
  font-size: 1rem;
  margin: 0.75rem 0 1rem 1rem;
  cursor: pointer;
  font-family: OptumSans, sans-serif;
  color: blue;
`;

export const HSBarWrapper = styled.div`
  width: 100%;
  margin-bottom: -0.375rem;
  #hsbar {
    border-bottom-right-radius: 0.36rem;
    border-bottom-left-radius: 0.36rem;
  }
`;

export const ThemeColorListWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow-y: scroll
  border-top: 1px solid ${defaultTheme.colors.aux.lightGrey};
  border-width: thin;
  font-family: OptumSans, sans-serif;
  font-size: 0.875rem;
`;

export const ThemeColorListItem = styled.div`
  display: flex;
  align-items: center;
  width: 50%;
  padding-left: 1rem;
`;

export const ThemeColorDisplayWrapper = styled.div`
  justify-items: center;
  align-items: center;
  text-align: center;
  width: 50%;
  padding: 0.5rem 0;
  display: flex;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledColorSquare = styled.div<{ colorValue: string; textColor: string }>`
  background: ${p => p.colorValue};
  height: 1.25rem;
  border-radius: 0.375rem;
  cursor: pointer;
  color: ${p => p.textColor};
  padding: 0 0.5rem;
  width: 100%;
  box-sizing: border-box;
  font-size: 10pt;
`;

const AddThemeStyledLink = styled(Text)`
  font-family: OptumSans, sans-serif;
  color: blue;
  font-size: 0.875rem;
  cursor: pointer;
  margin-left: 1.5rem;
`;

const CustomThemesSectionWrapper = styled.div``;

export interface SiteStylesProps {
  site: Site;
  updateSite: (site: Site) => void;
  saveSite: () => void;
  pages?: Array<Page>;
  updateCurrentPage?: (pageVersion: PageVersion) => void;
  setThemeType?: (themeType: ThemeType) => void;
  setTheme?: (themeType: HarmonyTheme | Theme | null) => void;
}

const getInitialCustomThemeList = (site: Site): CustomTheme[] => {
  const customThemeList: CustomTheme[] = [];

  if (!site.theme.customTheme1) {
    customThemeList.push(DEFAULT_CUSTOM_THEME_DATA);
    return customThemeList;
  }

  if (site.theme.customTheme1) {
    customThemeList.push({
      key: 1,
      name: site.theme.customTheme1.name || 'Custom Theme 1',
      colors: {
        ...(site.theme.customTheme1.colors || {
          primary: DEFAULT_CUSTOM_THEME_DATA.colors.primary,
          secondary: DEFAULT_CUSTOM_THEME_DATA.colors.secondary,
          tertiary: DEFAULT_CUSTOM_THEME_DATA.colors.tertiary,
          quaternary: DEFAULT_CUSTOM_THEME_DATA.colors.quaternary,
          custom: DEFAULT_CUSTOM_THEME_DATA.colors.custom,
        }),
      },
    });
  }

  if (site.theme.customTheme2) {
    customThemeList.push({
      key: 2,
      name: site.theme.customTheme2.name || 'Custom Theme 2',
      colors: {
        ...(site.theme.customTheme2.colors || {
          primary: DEFAULT_CUSTOM_THEME_DATA.colors.primary,
          secondary: DEFAULT_CUSTOM_THEME_DATA.colors.secondary,
          tertiary: DEFAULT_CUSTOM_THEME_DATA.colors.tertiary,
          quaternary: DEFAULT_CUSTOM_THEME_DATA.colors.quaternary,
          custom: DEFAULT_CUSTOM_THEME_DATA.colors.custom,
        }),
      },
    });
  }

  if (site.theme.customTheme3) {
    customThemeList.push({
      key: 3,
      name: site.theme.customTheme3.name || 'Custom Theme 3',
      colors: {
        ...(site.theme.customTheme3.colors || {
          primary: DEFAULT_CUSTOM_THEME_DATA.colors.primary,
          secondary: DEFAULT_CUSTOM_THEME_DATA.colors.secondary,
          tertiary: DEFAULT_CUSTOM_THEME_DATA.colors.tertiary,
          quaternary: DEFAULT_CUSTOM_THEME_DATA.colors.quaternary,
          custom: DEFAULT_CUSTOM_THEME_DATA.colors.custom,
        }),
      },
    });
  }

  if (customThemeList.length < 3) {
    customThemeList.forEach((theme, index) => {
      theme.key = index + 1;
    });
  }

  return customThemeList;
};

const getInitialCustomThemeCount = (site: Site): number => {
  const themeKeys = ['customTheme1', 'customTheme2', 'customTheme3'];
  const count = themeKeys.filter(key => (site.theme as any)[key]).length;
  return Math.min(count || 1, CUSTOM_THEME_LIMIT);
};

const BRAND_BASE: Record<string, Theme> = {
  optum: horizonOptumTheme,
  uhc: horizonUhcTheme,
  rally: horizonRallyTheme,
  dark: darkTheme,
};

const pickBaseTheme = (themeKey: string): Theme => {
  return BRAND_BASE[themeKey] ?? horizonOptumTheme;
};

const byIdFromCustom = (theme: Theme, id: string, fallback: string) => {
  const map: Record<string, string> = Object.fromEntries(
    (theme.colors?.custom ?? []).map(c => [c.id, c.value])
  );
  return map[id] ?? fallback;
};

/**
 * Build widget/page tokens from a theme key + site overrides.
 * Guarantees text colors via ensureThemeText().
 */
const getTokensForWidgets = (themeKey: string, site: Site) => {
  let base = pickBaseTheme(themeKey);

  if (
    /^customTheme\d+$/.test(themeKey) &&
    (site?.theme as any)?.[themeKey]?.colors
  ) {
    const c = (site.theme as any)[themeKey].colors;
    base = {
      ...base,
      colors: {
        ...base.colors,
        primary: c.primary ?? base.colors.primary,
        secondary: c.secondary ?? base.colors.secondary,
        tertiary: c.tertiary ?? base.colors.tertiary,
        quaternary: c.quaternary ?? base.colors.quaternary,
        custom: c.custom ?? base.colors.custom,
      },
      font: site.theme.font ?? base.font,
    };
  }

  // Normalize text colors
  base = ensureThemeText(base);

  const textPrimary = base.colors.text!.primary;
  const textSecondary = base.colors.text!.secondary;

  const font =
    base.font?.family?.base?.value ??
    (themeKey === 'uhc' ? 'UHCSans' : 'OptumSans');

  // Defaults for light
  let tokens = {
    font,
    textPrimary,
    textSecondary,
    pageBg: '#ffffff',
    surfaceBg: '#ffffff',
    link: base.colors?.primary ?? '#0C55B8',
    linkHover: base.colors?.tertiary ?? base.colors?.secondary ?? '#003A8D',
    hoverBg: base.colors?.tertiary ?? base.colors?.secondary ?? '#001D5B',
    buttonBg: base.colors?.primary ?? '#0C55B8',
    buttonHoverBg:
      base.colors?.quaternary ?? base.colors?.secondary ?? '#003A8D',
    buttonText: '#ffffff',
    buttonTextHover: '#ffffff',
  };

  if (themeKey === 'dark') {
    tokens = {
      ...tokens,
      pageBg: byIdFromCustom(base, 'pageBackground', '#121212'),
      surfaceBg: byIdFromCustom(base, 'cardBackground', '#1E1E1E'),
      link: byIdFromCustom(base, 'link', '#8AB4F8'),
      linkHover: byIdFromCustom(base, 'linkHover', '#AECBFA'),
      hoverBg: byIdFromCustom(base, 'buttonHover', '#985EFF'),
      buttonBg: byIdFromCustom(base, 'buttonBackground', '#BB86FC'),
      buttonHoverBg: byIdFromCustom(base, 'buttonHover', '#985EFF'),
      buttonText: byIdFromCustom(base, 'buttonText', '#000000'),
      buttonTextHover: byIdFromCustom(base, 'buttonTextHover', '#000000'),
      textPrimary, // from ensureThemeText (will be white)
      textSecondary, // from ensureThemeText (will be grey)
    };
  }

  return tokens;
};

export const SiteStylesPanel: React.FC<SiteStylesProps> = (
  props: SiteStylesProps
) => {
  const {
    updateSite,
    site,
    pages,
    updateCurrentPage,
    saveSite,
    setTheme,
    setThemeType,
  } = props;

  const copyExistingColorsToCustomTheme1 = () => {
    if (!site.theme.customTheme1) {
      const existingColors = site.theme.colors.custom;
      const customTheme1: CustomTheme = {
        key: 1,
        name: 'Custom Theme 1',
        colors: {
          primary: site.theme.colors.primary,
          secondary: site.theme.colors.secondary,
          tertiary: site.theme.colors.tertiary,
          quaternary: site.theme.colors.quaternary || '#D14600',
          custom: existingColors || [],
        },
      };

      const updatedSite: Site = {
        ...site,
        theme: {
          ...site.theme,
          customTheme1,
          enabledTheme: 'customTheme1',
        },
      };

      updateSite(updatedSite);
    }
  };

  // --- UI State
  const [stylePanelOpen, setStylePanelOpen] = useState(false);
  const [colorPanelOpen, setColorPanelOpen] = useState(false);
  const [brandPanelOpen, setBrandPanelOpen] = useState(false);
  const [brandTheme, setBrandTheme] = useState<any[]>([]);
  const [enabledTheme, setEnabledTheme] = useState<string>(
    site.theme.enabledTheme
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [fontFamily, setFontFamily] = useState<string>(
    site.theme?.font?.family?.base?.value ?? 'OptumSans'
  );
  const [fontSize, setFontSize] = useState<string>(
    String(site.theme?.font?.size?.body?.base?.value ?? 16)
  );
  const [activeColor, setActiveColor] = useState('');
  const [activeColorName, setActiveColorName] = useState('');
  const [activeColorType, setActiveColorType] = useState('');
  const [editColorsLinkId, setEditColorsLinkId] = useState(
    'edit-colors-link-1'
  );

  const [customThemeList, setCustomThemeList] = useState(
    getInitialCustomThemeList(site)
  );
  const [customThemeCount, setCustomThemeCount] = useState(
    getInitialCustomThemeCount(site)
  );

  const initializeColors = (): Color[] => {
    const themeMap: Record<string, keyof Site['theme']> = {
      'edit-colors-link-1': 'customTheme1',
      'edit-colors-link-2': 'customTheme2',
      'edit-colors-link-3': 'customTheme3',
    };

    const themeKey = themeMap[editColorsLinkId];

    if (themeKey === 'customTheme1' && !site.theme.customTheme1) {
      copyExistingColorsToCustomTheme1();
      return defaultAndCustomColors(site.theme.customTheme1 as any);
    }

    if (themeKey && (site.theme as any)[themeKey]) {
      return defaultAndCustomColors((site.theme as any)[themeKey]);
    }

    return defaultAndCustomColors({
      colors: DEFAULT_CUSTOM_THEME_DATA.colors,
    });
  };

  const [colors, setColors] = useState<Color[]>(initializeColors());

  const handleColorSave = (
    compProps: any,
    actColor: string,
    actColorName: string,
    actColorType: string,
    linkId: string
  ) => {
    setAnchorEl(null);
    useHandleColorSave(compProps, actColor, actColorName, actColorType, linkId);
  };

  useEffect(() => {
    const themeKey = determineThemeKeyFromEditColorsLinkId(editColorsLinkId);
    setColors(defaultAndCustomColors((site.theme as any)[themeKey]));
  }, [site.theme, editColorsLinkId]);

  useEffect(() => {
    setEnabledTheme(site.theme.enabledTheme);
  }, [site.theme.enabledTheme]);

  useEffect(() => {
    if (!site.theme.customTheme1) {
      copyExistingColorsToCustomTheme1();
      setEnabledTheme('customTheme1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickerOpen = Boolean(anchorEl);
  const id = pickerOpen ? 'simple-popover' : undefined;

  const toggleStylePanel = (open: boolean) => setStylePanelOpen(open);

  const handleColorClick = (
    e: React.MouseEvent<HTMLDivElement>,
    value?: string,
    name?: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    if (value) setActiveColor(value);
    if (name) setActiveColorName(name);
    setActiveColorType('Custom');
  };

  const handleColorClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(null);
  };

  const openColorPanel = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setStylePanelOpen(true);
    setColorPanelOpen(true);
  };

  const closeColorPanel = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setColorPanelOpen(false);
    setStylePanelOpen(true);
  };

  const closeAllPanels = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setColorPanelOpen(false);
    setStylePanelOpen(false);
    setBrandPanelOpen(false);
  };

  const openBrandPanel = (e: React.MouseEvent<any>, themeArr: any[]) => {
    e.stopPropagation();
    setBrandTheme(themeArr);
    setBrandPanelOpen(true);
  };

  const closeBrandPanel = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setBrandPanelOpen(false);
  };

  const handleFontChange = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    setFontFamily(e.target.value);

    updateSite({
      ...site,
      theme: {
        ...site.theme,
        font: {
          ...site.theme.font,
          family: {
            ...site.theme.font?.family,
            base: {
              ...site.theme.font?.family?.base,
              value: e.target.value,
            },
          },
        },
      },
    });
  };

  const handleFontSizeChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setFontSize(e.target.value);
    updateSite({
      ...site,
      theme: {
        ...site.theme,
        font: {
          ...site.theme.font,
          size: {
            ...site.theme.font?.size,
            body: {
              ...site.theme.font?.size?.body,
              base: {
                ...site.theme.font?.size?.body?.base,
                value: parseFloat(e.target.value),
              },
            },
          },
        },
      },
    });
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const getThemeDisplayName = (current: any) => {
    const themeNameMap = new Map<any, string>([
      [uhcTheme, 'UHC Theme'],
      [optumTheme, 'Optum Theme'],
      [rallyTheme, 'Rally Theme'],
      [darkTheme, 'Dark Mode'],
    ]);
    for (const [ref, name] of themeNameMap.entries()) {
      if (current === ref) return name;
    }
    return 'Brand Theme';
  };

  const selectCustomTheme = (selection: string) => {
    setEnabledTheme(selection);
    updateSite({
      ...site,
      theme: {
        ...site.theme,
        enabledTheme: selection,
      },
    });
    updatePagesWithTheme(selection);
  };

  const updatePagesWithTheme = (themeKey: string) => {
    const t = getTokensForWidgets(themeKey, site);

    pages?.forEach(page => {
      page.current.pageBackground = {
        style: 'Color',
        color: t.pageBg,
        gradient: '',
      };

      Object.keys(page.current.content).forEach(widgetId => {
        const widget = page.current.content[widgetId];
        const config = widget.config || {};

        switch (widget.type) {
          // content/surface widgets
          case widgetTypes.Text:
          case widgetTypes.Navigation:
          case widgetTypes.AccordionWidget:
          case widgetTypes.Tabs:
          case widgetTypes.TextInput:
          case widgetTypes.RadioButton:
          case widgetTypes.Checkbox:
          case widgetTypes.Dropdown:
          case widgetTypes.FileUploader:
          case widgetTypes.Image:
          case widgetTypes.VideoUpload:
          case widgetTypes.Divider:
          case widgetTypes.Html:
          case widgetTypes.Section:
          case widgetTypes.Calendar:
          case widgetTypes.Application:
          case widgetTypes.Slideshow:
          case widgetTypes.Banner:
          case widgetTypes.Form:
          case widgetTypes.RichText:
          case widgetTypes.UploadWidget:
          case widgetTypes.CalendarWidget:
          case widgetTypes.TemplateHeaderFooterPlaceholder:
          case widgetTypes.NotificationWidget:
          case widgetTypes.Card:
            widget.config = {
              ...config,
              font: t.font,
              textColor: t.textPrimary,
              backgroundData: { style: 'Color', color: t.surfaceBg },
            };
            break;

          case widgetTypes.Search:
            widget.config = {
              ...config,
              font: t.font,
              textColor: t.textPrimary,
              backgroundData: { style: 'Color', color: t.pageBg },
            };
            break;

          case widgetTypes.Button:
            widget.config = {
              ...config,
              font: t.font,
              textColor: t.buttonText,
              backgroundData: { style: 'Color', color: t.buttonBg },
              hoverData: {
                ...config.hoverData,
                font: t.font,
                backgroundData: { style: 'None', color: t.buttonHoverBg },
                textColor: t.buttonTextHover,
              },
            };
            break;

          case widgetTypes.LoginButton:
            widget.config = {
              ...config,
              font: t.font,
              textColor: t.textPrimary,
              backgroundData: { style: 'Color', color: t.pageBg },
              hoverData: {
                ...defaultLinkWidgetHoverData,
                font: t.font,
                backgroundData: { style: 'None', color: t.hoverBg },
              },
            };
            break;

          case widgetTypes.Link:
            widget.config = {
              ...config,
              textColor: t.link,
              iconColor: t.link,
              font: t.font,
              backgroundData: { style: 'Color', color: t.pageBg },
              hoverData: {
                ...config.hoverData,
                textColor: t.linkHover,
                font: t.font,
                fontFamily: t.font,
                underline: true,
              },
            };
            break;

          default:
            break;
        }
      });

      updateCurrentPage?.(page.current);
    });
  };

  const displayColorName = (color: Color) => {
    if (!color?.value) return '';
    switch (true) {
      case color.value.startsWith('radial'):
      case color.value.startsWith('linear'):
        return '';
      case color.value.startsWith('rgb'):
        return convertToHex(color.value);
      default:
        return color.value;
    }
  };

  const incrementCustomThemeCount = () => {
    setCustomThemeCount(prev => prev + 1);
    updateSite({
      ...site,
      theme: {
        ...site.theme,
        customThemeCount: (site.theme as any).customThemeCount + 1 || 2,
      },
    });
  };

  const decrementCustomThemeCount = () => {
    if (customThemeCount > 1) {
      setCustomThemeCount(prev => prev - 1);
      updateSite({
        ...site,
        theme: {
          ...site.theme,
          customThemeCount: customThemeCount - 1,
        },
      });
    }
  };

  const handleAddCustomThemeClick = (e: React.MouseEvent<any>) => {
    e.preventDefault();
    e.stopPropagation();
    const nextCount = customThemeCount + 1;
    incrementCustomThemeCount();

    const newTheme: CustomTheme = {
      key: nextCount,
      name: `Custom Theme ${nextCount}`,
      colors: {
        primary: '#ff0000',
        secondary: '#00ff00',
        tertiary: '#0000ff',
        custom: [],
      },
    };

    const updatedThemeList = [...customThemeList, newTheme].map((t, i) => ({
      ...t,
      key: i + 1,
    }));
    setCustomThemeList(updatedThemeList);

    updateSite({
      ...site,
      theme: {
        ...site.theme,
        [`customTheme${nextCount}`]: newTheme,
        customThemeCount: nextCount,
      } as any,
    });
  };

  const handleDeleteCustomTheme = (id: number) => {
    const updatedThemeList = customThemeList
      .filter(theme => theme.key !== id)
      .map((t, i) => ({
        ...t,
        key: i + 1,
      }));
    setCustomThemeList(updatedThemeList);
    decrementCustomThemeCount();

    const updatedSite = { ...site } as any;
    updatedSite.theme.customThemeCount = customThemeCount - 1;

    for (let i = id; i < customThemeCount; i++) {
      updatedSite.theme[`customTheme${i}`] =
        updatedSite.theme[`customTheme${i + 1}`];
    }
    delete updatedSite.theme[`customTheme${customThemeCount}`];

    if (site.theme.enabledTheme === `customTheme${id}`) {
      updatedSite.theme.enabledTheme = 'customTheme1';
    }

    updateSite(updatedSite);
  };

  const handleOpenColorPanel = (e: React.MouseEvent<any>) => {
    e.preventDefault();
    e.stopPropagation();

    const linkId = e.currentTarget.getAttribute('id') || 'edit-colors-link-1';
    setEditColorsLinkId(linkId);
    setStylePanelOpen(true);
    setColorPanelOpen(true);

    const themeKey = determineThemeKeyFromEditColorsLinkId(linkId);
    setColors(defaultAndCustomColors((site.theme as any)[themeKey]));
  };

  const fetchCustomThemeHSBarData = (linkId: string) => {
    const customThemeData = [
      { value: 38, color: DEFAULT_CUSTOM_THEME_DATA.colors.custom[0].value },
      { value: 32, color: DEFAULT_CUSTOM_THEME_DATA.colors.custom[1].value },
      { value: 24, color: DEFAULT_CUSTOM_THEME_DATA.colors.custom[2].value },
      { value: 16, color: DEFAULT_CUSTOM_THEME_DATA.colors.custom[3].value },
    ];

    const themeKey = determineThemeKeyFromEditColorsLinkId(linkId);
    if (!themeKey) return customThemeData;

    const themeColors = (site.theme as any)[themeKey]?.colors?.custom as
      | Color[]
      | undefined;
    themeColors?.forEach((c, idx) => {
      if (idx < customThemeData.length) {
        customThemeData[idx].color = c.value;
      }
    });

    return customThemeData;
  };
  // Handles selection of a theme by key, updates site and page themes accordingly
  const handleThemeSelect = (themeKey: string) => {
    const selected = getAvailableThemes(site).find(t => t.key === themeKey);
    if (!selected) {
      console.error('Theme selection failed: invalid themeKey', themeKey);
      return;
    }

    // Update global theme type and theme instance for UI/state
    const typeToUse = selected.type ?? ThemeType.Optum;
    setThemeType(typeToUse);
    // Only set Harmony theme instances that exist in the harmonyThemeMap
    const harmonyTheme = harmonyThemeMap.get(typeToUse as any);
    if (harmonyTheme) {
      setTheme(harmonyTheme);
    }

    // Persist enabled theme and, for custom themes, store the theme under its key
    updateSite({
      ...site,
      theme: {
        ...site.theme,
        enabledTheme: themeKey,
        ...(selected.type ? {} : { [themeKey]: selected.theme }),
      },
    });

    // Cascade theme token updates across pages and widgets
    updatePagesWithTheme(themeKey);
  };
  return (
    <>
      <StyledMenuItem
        data-test-id="style-menu"
        icon={SiteStyles}
        onClick={() => toggleStylePanel(true)}
        rootStyles={{
          ['& > .' + menuClasses.button]: {
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#FFFFFF',
            },
            '&:focus': {
              backgroundColor: '#D9F6FA',
            },
          },
          ['.' + menuClasses.subMenuContent]: {
            backgroundColor: '#FFFFFF',
            width: stylePanelOpen || colorPanelOpen ? '16.5rem' : '0px',
            margin: '-3px',
          },
          [`.${menuClasses.icon}`]: {
            '&:hover': {
              backgroundColor: '#d9f6fa',
              fill: '#002677',
            },
          },
        }}
        title="Site Styles"
      >
        {(colorPanelOpen === false && brandPanelOpen === false && (
          <div
            data-test-id="color-select-panel"
            style={{ height: '775px', overflowY: 'scroll' }}
          >
            <TopSection>
              <StyledHeading data-test-id="site-styles-panel-heading">
                Site Styles
              </StyledHeading>
              <SharedIcon
                data-test-id="site-styles-panel-collapse-icon"
                id="top-section"
                icon="Collapse"
                onClick={() => toggleStylePanel(false)}
                width="1.375rem"
                style={{ marginLeft: '5.67rem', cursor: 'pointer' }}
              />
            </TopSection>
            <div
              style={
                !site.access.edit
                  ? { pointerEvents: 'none', opacity: '0.4' }
                  : {}
              }
            >
              <FontDropdowns>
                <FormControl
                  data-test-id="font-dropdown-form"
                  id={'controlled-select'}
                >
                  <FieldLabel>Font Family</FieldLabel>
                  <FontSelect
                    data-test-id="font-dropdown"
                    onChange={handleFontChange}
                    value={fontFamily}
                  >
                    {Object.keys(getCombinedFontsForTheme()).map(f => {
                      return (
                        <option value={f} key={f}>
                          {f}
                        </option>
                      );
                    })}
                  </FontSelect>
                </FormControl>
                <FontSizeInputWrapper>
                  <FontSizeInput
                    id="site-styles-font-size"
                    value={fontSize}
                    onChange={handleFontSizeChange}
                    onKeyDown={handleNumberInput}
                    data-test-id="site-styles-font-size"
                    type="number"
                    min={0}
                  />
                  <FontSizeUnitStyle>px</FontSizeUnitStyle>
                </FontSizeInputWrapper>
              </FontDropdowns>

              <FieldLabel data-test-id="custom-themes-section-header">
                Custom Themes
              </FieldLabel>
              <CustomThemesSectionWrapper>
                <CustomThemesSection
                  site={site}
                  updateSite={updateSite}
                  pages={pages}
                  updateCurrentPage={updateCurrentPage}
                  openColorPanel={openColorPanel}
                  selectCustomTheme={selectCustomTheme}
                  enabledTheme={enabledTheme}
                  customThemeCount={customThemeCount}
                  customThemeList={customThemeList}
                  decrementCustomThemeCount={decrementCustomThemeCount}
                  incrementCustomThemeCount={incrementCustomThemeCount}
                  handleDeleteCustomTheme={handleDeleteCustomTheme}
                  handleOpenColorPanel={handleOpenColorPanel}
                />
                {customThemeCount < CUSTOM_THEME_LIMIT ? (
                  <AddThemeStyledLink
                    data-test-id="custom-themes-counter-button"
                    onClick={handleAddCustomThemeClick}
                  >
                    +Add another custom theme
                  </AddThemeStyledLink>
                ) : (
                  <div
                    style={{
                      width: 'fit-content',
                      padding: '0.5rem',
                      color: '#D14600',
                      fontFamily: 'OptumSans, sans-serif',
                      fontSize: '14px',
                      fontWeight: '400',
                      marginLeft: '1rem',
                    }}
                  >
                    Horizon only allows up to {CUSTOM_THEME_LIMIT} custom themes
                  </div>
                )}
              </CustomThemesSectionWrapper>

              <FieldLabel data-test-id="brand-themes-header">
                Brand Themes
              </FieldLabel>

              {getAvailableThemes(site).map(({ key, name, theme }) => (
                <ThemeColorCard key={key}>
                  <ColorCardTop>
                    <FieldLabel>{name}</FieldLabel>
                    <ThemeSelectButton
                      data-test-id={`${key}-theme-button`}
                      variant={enabledTheme === key ? 'secondary' : 'tertiary'}
                      onPress={() => handleThemeSelect(key)}
                      disabled={!site.access.edit}
                      themeEnabled={enabledTheme === key}
                    >
                      {enabledTheme === key ? 'Theme in use' : 'Use this theme'}
                    </ThemeSelectButton>
                  </ColorCardTop>

                  <LinkWrapper>
                    <StyledLink
                      data-test-id={`view-${key}-theme-link`}
                      onClick={e =>
                        openBrandPanel(e, theme.colors?.custom ?? [])
                      }
                    >
                      View colors {'>'}
                    </StyledLink>
                  </LinkWrapper>

                  <HSBarWrapper>
                    <StyledHSBar
                      data-test-id="color-bar"
                      height="1.5rem"
                      data={
                        key === 'optum'
                          ? optumThemeBar
                          : key === 'uhc'
                          ? uhcThemeBar
                          : key === 'rally'
                          ? rallyThemeBar
                          : key === 'dark'
                          ? darkThemeBar
                          : fetchCustomThemeHSBarData(
                              `edit-colors-link-${key.slice(-1)}`
                            )
                      }
                    />
                  </HSBarWrapper>
                </ThemeColorCard>
              ))}
            </div>
          </div>
        )) ||
          (colorPanelOpen === true && (
            <div
              data-test-id="color-picker-panel"
              style={{ height: '775px', overflowY: 'scroll' }}
            >
              <TopSection>
                <StyledHeading data-test-id="color-panel-open-heading">
                  Site Styles
                </StyledHeading>
                <SharedIcon
                  data-test-id="collapse-icon"
                  id="top-section"
                  icon="Collapse"
                  onClick={e => closeAllPanels(e)}
                  width="1.375rem"
                  style={{ marginLeft: '5.67rem', cursor: 'pointer' }}
                />
              </TopSection>
              <br />
              <StyledMenuLink
                data-test-id="return-to-menu-link"
                onClick={e => closeColorPanel(e)}
              >
                {'< '}Back to Theme{' '}
              </StyledMenuLink>
              <FieldLabel data-test-id="edit-colors-custom-theme-header">
                Custom Theme
              </FieldLabel>
              <ThemeColorCard>
                <ColorCardTop>
                  <FieldLabel data-test-id="edit-colors-custom-theme-card-header">
                    Site Theme
                  </FieldLabel>
                  <ThemeSelectButton
                    data-test-id="theme-button-disabled"
                    disabled
                    themeEnabled={
                      enabledTheme ===
                      determineThemeKeyFromEditColorsLinkId(editColorsLinkId)
                    }
                  >
                    {enabledTheme ===
                    determineThemeKeyFromEditColorsLinkId(editColorsLinkId)
                      ? 'Theme in use'
                      : 'Use this theme'}
                  </ThemeSelectButton>
                </ColorCardTop>
                <HSBarWrapper>
                  <StyledHSBar
                    id="custom-theme-color-bar"
                    data-test-id="color-bar"
                    height="1.5rem"
                    data={fetchCustomThemeHSBarData(editColorsLinkId)}
                  />
                </HSBarWrapper>
              </ThemeColorCard>
              <StyledPickerPopover
                data-test-id="color-picker-popover"
                id={id}
                open={pickerOpen}
                onClick={handleClick}
                onClose={handleColorClose}
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
              >
                <StyledPickerWrapper>
                  <StyledPickerHeader data-test-id="styled-picker-header">
                    Color Selector
                  </StyledPickerHeader>
                  <StyledClose
                    data-test-id="close-icon"
                    onClick={handleColorClose}
                  />
                </StyledPickerWrapper>
                <StylePanelGradientColorPicker
                  activeColor={activeColor}
                  activeColorName={activeColorName}
                  activeColorType={activeColorType}
                  editColorsLinkId={editColorsLinkId}
                  stylePanelProps={props}
                  site={site}
                  handleColorSave={handleColorSave}
                  updateSite={updateSite}
                  saveSite={saveSite}
                  anchorEl={anchorEl}
                />
              </StyledPickerPopover>
              {colors.map(color => (
                <div
                  key={`${color.id || color.name}-${color.value}`}
                  data-test-id="color-panel-display"
                >
                  <ThemeColorListWrapper>
                    <ThemeColorListItem data-test-id="color-list-title">
                      {(color as any).name}
                    </ThemeColorListItem>
                    <ThemeColorDisplayWrapper>
                      <StyledColorSquare
                        colorValue={color.value}
                        textColor={textColor(color.value)}
                        key={color.id}
                        id={color.id}
                        data-test-id="color-list-square"
                        onClick={e =>
                          handleColorClick(e, color.value, (color as any).name)
                        }
                      >
                        {displayColorName(color)}
                      </StyledColorSquare>
                    </ThemeColorDisplayWrapper>
                  </ThemeColorListWrapper>
                </div>
              ))}
              <StyledLink
                data-test-id="add-color-link"
                style={{ paddingLeft: '1rem', width: '100%' }}
                onClick={handleColorClick}
              >
                + Add another color
              </StyledLink>
            </div>
          )) ||
          (brandPanelOpen === true && (
            <div data-test-id="brand-theme-panel" style={{ height: '775px' }}>
              <TopSection>
                <StyledHeading data-test-id="brand-panel-open-heading">
                  Site Styles
                </StyledHeading>
                <SharedIcon
                  data-test-id="collapse-icon"
                  id="top-section"
                  icon="Collapse"
                  onClick={e => closeAllPanels(e)}
                  width="1.375rem"
                  style={{ marginLeft: '5.67rem', cursor: 'pointer' }}
                />
              </TopSection>
              <br />
              <StyledMenuLink
                data-test-id="styled-link"
                onClick={closeBrandPanel}
              >
                {'< '}Back to Theme{' '}
              </StyledMenuLink>
              <FieldLabel
                data-test-id={`${brandTheme || 'default-brand'}-header`}
              >
                {getThemeDisplayName(brandTheme)}
              </FieldLabel>
              {brandTheme.map(({ value, name }: any) => (
                <div key={name}>
                  <ThemeColorListWrapper>
                    <ThemeColorListItem data-test-id="color-list-title">
                      {name}
                    </ThemeColorListItem>
                    <ThemeColorDisplayWrapper>
                      <StyledColorSquare
                        id={value}
                        colorValue={value}
                        textColor={textColor(value)}
                      >
                        {value}
                      </StyledColorSquare>
                    </ThemeColorDisplayWrapper>
                  </ThemeColorListWrapper>
                </div>
              ))}
            </div>
          ))}
      </StyledMenuItem>
    </>
  );
};
